// ═══════════════════════════════════════════════════════════════
// ClearView CRM Color Palettes — CDP 5-Palette Design System
// Updated: Apr 2026 — Toolbar accent tinting for navigation focus
//
// Text token roles:
//   text1 — Primary: headings, names, values (#111827 — near black)
//   text2 — Secondary: labels, descriptions (#6b7280 — medium gray)
//   text3 — Tertiary: helper text, timestamps, captions (#9ca3af — light gray)
//   accent — Single accent for buttons, active states, badges
//
// Toolbar tokens:
//   toolbarBg     — Background gradient tinted with accent color
//   toolbarBorder — Bottom border color (accent-tinted)
//   toolbarShadow — Drop shadow for depth/focus
//
// Design principle: ALL user-facing text must be clearly readable.
// text2 provides visual separation from text1 for labels/descriptions.
// text3 is for helper text — lighter but still readable on card backgrounds.
// ═══════════════════════════════════════════════════════════════

export const PALETTES = {
  violet: {
    id: "violet",
    name: "Violet Dream",
    preview: "linear-gradient(135deg, #667eea, #764ba2)",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    headerBg: "rgba(255,255,255,0.12)",
    toolbarBg: "linear-gradient(180deg, rgba(102,126,234,0.10) 0%, rgba(102,126,234,0.04) 100%)",
    toolbarBorder: "rgba(102,126,234,0.20)",
    toolbarShadow: "0 4px 16px rgba(102,126,234,0.10), 0 2px 4px rgba(0,0,0,0.06)",
    cardBg: "rgba(255,255,255,0.95)",
    cardBorder: "rgba(0,0,0,0.08)",
    text1: "#111827",
    text2: "#4b5563",
    text3: "#6b7280",
    textOnGrad: "#ffffff",
    inputBg: "#ffffff",
    inputColor: "#1f2937",
    inputBorder: "#d1d5db",
    focusBorder: "#667eea",
    focusShadow: "0 0 0 3px rgba(102,126,234,0.15)",
    accent: "#667eea",
    accent1: "#667eea",
    accent2: "#764ba2",
    tagDonor: "#8b5cf6",
    tagBoard: "#ef4444",
    tagCfre: "#d97706",
  },
  oceanic: {
    id: "oceanic",
    name: "Ocean Breeze",
    preview: "linear-gradient(135deg, #2193b0, #6dd5ed)",
    background: "linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)",
    headerBg: "rgba(255,255,255,0.12)",
    toolbarBg: "linear-gradient(180deg, rgba(33,147,176,0.10) 0%, rgba(33,147,176,0.04) 100%)",
    toolbarBorder: "rgba(33,147,176,0.20)",
    toolbarShadow: "0 4px 16px rgba(33,147,176,0.10), 0 2px 4px rgba(0,0,0,0.06)",
    cardBg: "rgba(255,255,255,0.95)",
    cardBorder: "rgba(0,0,0,0.08)",
    text1: "#111827",
    text2: "#4b5563",
    text3: "#6b7280",
    textOnGrad: "#ffffff",
    inputBg: "#ffffff",
    inputColor: "#1f2937",
    inputBorder: "#d1d5db",
    focusBorder: "#2193b0",
    focusShadow: "0 0 0 3px rgba(33,147,176,0.15)",
    accent: "#2193b0",
    accent1: "#2193b0",
    accent2: "#6dd5ed",
    tagDonor: "#6366f1",
    tagBoard: "#ef4444",
    tagCfre: "#d97706",
  },
  sunset: {
    id: "sunset",
    name: "Sunset Glow",
    preview: "linear-gradient(135deg, #f2994a, #f2668b)",
    background: "linear-gradient(135deg, #f2994a 0%, #f2668b 100%)",
    headerBg: "rgba(255,255,255,0.12)",
    toolbarBg: "linear-gradient(180deg, rgba(242,153,74,0.10) 0%, rgba(242,153,74,0.04) 100%)",
    toolbarBorder: "rgba(242,153,74,0.20)",
    toolbarShadow: "0 4px 16px rgba(242,153,74,0.10), 0 2px 4px rgba(0,0,0,0.06)",
    cardBg: "rgba(255,255,255,0.95)",
    cardBorder: "rgba(0,0,0,0.08)",
    text1: "#111827",
    text2: "#4b5563",
    text3: "#6b7280",
    textOnGrad: "#ffffff",
    inputBg: "#ffffff",
    inputColor: "#1f2937",
    inputBorder: "#d1d5db",
    focusBorder: "#f2994a",
    focusShadow: "0 0 0 3px rgba(242,153,74,0.15)",
    accent: "#f2994a",
    accent1: "#f2994a",
    accent2: "#f2668b",
    tagDonor: "#8b5cf6",
    tagBoard: "#dc2626",
    tagCfre: "#b45309",
  },
  forest: {
    id: "forest",
    name: "Forest Green",
    preview: "linear-gradient(135deg, #11998e, #38ef7d)",
    background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
    headerBg: "rgba(255,255,255,0.12)",
    toolbarBg: "linear-gradient(180deg, rgba(17,153,142,0.10) 0%, rgba(17,153,142,0.04) 100%)",
    toolbarBorder: "rgba(17,153,142,0.20)",
    toolbarShadow: "0 4px 16px rgba(17,153,142,0.10), 0 2px 4px rgba(0,0,0,0.06)",
    cardBg: "rgba(255,255,255,0.95)",
    cardBorder: "rgba(0,0,0,0.08)",
    text1: "#111827",
    text2: "#4b5563",
    text3: "#6b7280",
    textOnGrad: "#ffffff",
    inputBg: "#ffffff",
    inputColor: "#1f2937",
    inputBorder: "#d1d5db",
    focusBorder: "#11998e",
    focusShadow: "0 0 0 3px rgba(17,153,142,0.15)",
    accent: "#11998e",
    accent1: "#11998e",
    accent2: "#38ef7d",
    tagDonor: "#7c3aed",
    tagBoard: "#ef4444",
    tagCfre: "#d97706",
  },
  midnight: {
    id: "midnight",
    name: "Midnight Sky",
    preview: "linear-gradient(135deg, #0f0c29, #302b63, #24243e)",
    background:
      "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)",
    headerBg: "rgba(255,255,255,0.08)",
    toolbarBg: "linear-gradient(180deg, rgba(129,140,248,0.12) 0%, rgba(129,140,248,0.05) 100%)",
    toolbarBorder: "rgba(129,140,248,0.22)",
    toolbarShadow: "0 4px 16px rgba(129,140,248,0.12), 0 2px 4px rgba(0,0,0,0.08)",
    cardBg: "rgba(255,255,255,0.92)",
    cardBorder: "rgba(0,0,0,0.1)",
    text1: "#111827",
    text2: "#4b5563",
    text3: "#6b7280",
    textOnGrad: "#e2e8f0",
    inputBg: "#ffffff",
    inputColor: "#1f2937",
    inputBorder: "#d1d5db",
    focusBorder: "#818cf8",
    focusShadow: "0 0 0 3px rgba(129,140,248,0.15)",
    accent: "#818cf8",
    accent1: "#818cf8",
    accent2: "#6366f1",
    tagDonor: "#a78bfa",
    tagBoard: "#f87171",
    tagCfre: "#fbbf24",
  },
};
